import { useState } from 'react'

import { useMutation } from '@apollo/client'
import { Alert, AlertTitle } from '@canyon/ui/Alert'
import { Button } from '@canyon/ui/Button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  InnerDialog,
  InnerDialogClose,
  InnerDialogContent,
  InnerDialogFooter,
  InnerDialogHeader,
  InnerDialogTitle,
} from '@canyon/ui/Dialog'
import { Input } from '@canyon/ui/Input'
import { InputChips } from '@canyon/ui/InputChips'
import { Switch } from '@canyon/ui/Switch'
import { Tooltip, TooltipContent, TooltipTrigger } from '@canyon/ui/Tooltip'
import {
  VerticalGroup,
  VerticalGroupColumn,
  VerticalGroupDescription,
  VerticalGroupRow,
  VerticalGroupTitle,
} from '@canyon/ui/VerticalGroup'
import { useToast } from '@canyon/ui/useToast'
import { cn } from '@canyon/ui/utils'
import { AsteriskSimple, CheckCircle, Question } from '@phosphor-icons/react'
import { useRouter } from 'next/navigation'

import {
  ResumesList_ResumesDocument,
  TailorResumeDialog_CreateUsersJobDocument,
  TailorResumeDialog_FetchJobKeywordsDocument,
  TailorResumeDialog_FetchMissingSkillsOfJobResumeDocument,
  TailorResumeDialog_OptimizeResumeForJobDocument,
  UseCurrentUser_UsersDocument,
} from '@gql/graphql'

import { ApplicationSelect } from '@/components/ApplicationSelect'
import { ResumesSelect } from '@/components/ResumesSelect'
import { SuggestedChips } from '@/components/SuggestedChips'
import {
  CONDENSED_APPLICATION_FORM_ID,
  CondensedApplicationForm,
} from '@/forms/CondensedApplicationForm'
import { useApplicationForm } from '@/forms/hooks/useApplicationForm'
import { useUpgradePlanDialog } from '@/hooks/contexts/useUpgradePlanDialog'
import { useCurrentUser } from '@/hooks/useCurrentUser'

import { ATSInfoTooltip } from '../info-tooltips/ATSInfoTooltip'

interface ITailorResumeDialogProps {
  applicationId?: string
  jobListingId?: string
  open: boolean
  resumeId?: string
  setOpen: (open: boolean) => void
  showResumeSelection?: boolean
}

export const TailorResumeDialog = ({
  applicationId,
  jobListingId,
  open,
  resumeId,
  setOpen,
  showResumeSelection,
}: ITailorResumeDialogProps) => {
  const router = useRouter()
  const { errorToast, toast } = useToast()
  const { isPaidPlan, tokens } = useCurrentUser()
  const upgradePlanDialog = useUpgradePlanDialog()

  const [selectedResumeId, setSelectedResumeId] = useState<string>(resumeId ?? '')
  const [selectedApplicationId, setSelectedApplicationId] = useState<string>(applicationId ?? '')
  const [showKeywordsScreen, setShowKeywordsScreen] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [jobTitle, setJobTitle] = useState<string>('')
  const [isTailorJobTitle, setIsTailorJobTitle] = useState(false)
  const [isAutoSelectSkills, setIsAutoSelectSkills] = useState(false)

  const [suggestedJobKeywords, setSuggestedJobKeywords] = useState<string[]>([])
  const [selectedJobKeywords, setSelectedJobKeywords] = useState<string[]>([])

  const [missingSkills, setMissingSkills] = useState<string[]>([])
  const [skills, setSkills] = useState<string[]>([])

  const { form, resetForm } = useApplicationForm()

  const reset = () => {
    resetForm()
    setSelectedApplicationId(applicationId ?? '')
    setSelectedResumeId(resumeId ?? '')
    setJobTitle('')
    setShowKeywordsScreen(false)

    setSelectedJobKeywords([])
    setSuggestedJobKeywords([])
    setMissingSkills([])
    setSkills([])
  }

  const handleDialogOpenChange = (open: boolean) => {
    if (!open) {
      reset()
    }

    setOpen(open)
  }

  const isCreateNewApplication = selectedApplicationId === 'create'
  const [createApplication] = useMutation(TailorResumeDialog_CreateUsersJobDocument, {
    variables: {
      companyName: form.getValues().company,
      jobDetails: form.getValues().jobDetails,
      position: form.getValues().position,
      status: form.getValues().status,
    },
  })

  const [fetchJobKeywords] = useMutation(TailorResumeDialog_FetchJobKeywordsDocument)
  const [fetchMissingSkillsOfJobResume] = useMutation(
    TailorResumeDialog_FetchMissingSkillsOfJobResumeDocument
  )

  const handleFetchJobKeywordsAndMissingSkills = async ({
    applicationId,
    jobTitle,
  }: {
    applicationId: string
    jobTitle?: string
  }) => {
    try {
      const { data: keywordsData } = await fetchJobKeywords({
        variables: {
          jobListingId: jobListingId ?? '',
          jobTitle: isTailorJobTitle ? jobTitle : undefined,
          usersJobId: isTailorJobTitle ? undefined : applicationId,
        },
      })

      if (!isTailorJobTitle) {
        const { data: missingSkillsData } = await fetchMissingSkillsOfJobResume({
          variables: {
            jobListingId: jobListingId ?? '',
            resumeId: selectedResumeId,
            usersJobId: applicationId,
          },
        })
        setMissingSkills(missingSkillsData?.fetchMissingSkillsOfJobResume ?? [])
      } else {
        setMissingSkills([])
      }

      setSuggestedJobKeywords(keywordsData?.fetchJobKeywords ?? [])
    } catch (error) {
      errorToast({ description: 'There was an error fetching job keywords and missing skills.' })
    }
  }

  const handleCreationApplicationSubmit = async () => {
    setIsLoading(true)

    try {
      const { data } = await createApplication()

      if (data?.createUsersJob) {
        toast({ title: 'New job application created 🎉' })

        await handleFetchJobKeywordsAndMissingSkills({
          applicationId: data?.createUsersJob.id ?? '',
          jobTitle: undefined,
        })

        setSelectedApplicationId(data?.createUsersJob.id)
        setShowKeywordsScreen(true)
        resetForm()
      }
    } catch (error) {
      errorToast({ description: 'There was an error creating the application.' })
    } finally {
      setIsLoading(false)
    }
  }

  const handleInitialScreenSubmit = async () => {
    setIsLoading(true)

    try {
      await handleFetchJobKeywordsAndMissingSkills({
        applicationId: selectedApplicationId,
        jobTitle: jobTitle,
      })

      setShowKeywordsScreen(true)
    } finally {
      setIsLoading(false)
    }
  }

  const enableApplicationScreenContinueButton =
    (isCreateNewApplication && form.formState.isValid) ||
    (selectedApplicationId !== '' && selectedResumeId !== '' && !isTailorJobTitle) ||
    (jobTitle !== '' && selectedResumeId !== '' && isTailorJobTitle) ||
    (jobListingId && selectedResumeId !== '')

  const [optimizeResumeForJob] = useMutation(TailorResumeDialog_OptimizeResumeForJobDocument, {
    refetchQueries: [
      {
        query: ResumesList_ResumesDocument,
        variables: {
          aiGenerated: true,
          aiGeneratedForJobTitle: isTailorJobTitle,
          archived: false,
        },
      },
    ],
  })

  const handleOptimizeResumeForJob = async () => {
    if (!isPaidPlan && (tokens?.optimizeResume || 0) <= 0) {
      upgradePlanDialog.setOpen(true)
      return
    }

    setIsLoading(true)

    try {
      const { data } = await optimizeResumeForJob({
        refetchQueries: [UseCurrentUser_UsersDocument],
        variables: {
          jobListingId: jobListingId,
          jobTitle: isTailorJobTitle ? jobTitle : undefined,
          keywords: selectedJobKeywords,
          resumeId: selectedResumeId,
          skills: isAutoSelectSkills ? skills.concat(missingSkills.slice(0, 5)) : skills,
          usersJobId: isTailorJobTitle ? undefined : selectedApplicationId,
        },
      })

      if (data?.optimizeResumeForJobV2) {
        handleDialogOpenChange(false)
        router.push(`/resumes/${data?.optimizeResumeForJobV2?.id}`)
      }
    } catch (error) {
      errorToast({ description: 'There was an error optimizing your resume.' })
    } finally {
      setIsLoading(false)
    }
  }

  const handleSuggestedKeywordAdd = (keyword: string) => {
    setSelectedJobKeywords([...selectedJobKeywords, keyword])
    setSuggestedJobKeywords(
      suggestedJobKeywords.filter((suggestedKeyword) => suggestedKeyword !== keyword)
    )
  }

  const handleSuggestedSkillAdd = (skill: string) => {
    setSkills([...skills, skill])
    setMissingSkills(missingSkills.filter((suggestedSkill) => suggestedSkill !== skill))
  }

  const TailorStyleComponent = ({
    isSet,
    onClick,
    text,
  }: {
    isSet: boolean
    onClick: () => void
    text: string
  }) => {
    return (
      <div
        className={cn(
          'flex w-full lg:w-1/2 cursor-pointer flex-row justify-between rounded-md border p-3 lg:p-4',
          isSet ? 'border-border-secondary' : 'border-border-muted',
          !isSet && 'text-muted-foreground'
        )}
        onClick={onClick}
      >
        <span className="text-sm lg:text-base">{text}</span>
        {isSet && (
          <span className="flex items-center">
            <CheckCircle size={16} weight="fill" />
          </span>
        )}
        {!isSet && (
          <span className="border-border-muted flex h-[16px] w-[16px] items-center rounded-full border"></span>
        )}
      </div>
    )
  }

  return (
    <Dialog onOpenChange={handleDialogOpenChange} open={open}>
      <DialogContent
        className="max-w-[700px] w-[calc(100vw-2rem)] lg:w-full max-h-[90vh] overflow-y-auto"
        title="Optimize Resume for Job"
        titleIcon={<AsteriskSimple />}
      >
        <div className="p-3 lg:p-4">
          <Alert className="mb-6 lg:mb-8" variant="information">
            <AlertTitle className="text-xs lg:text-sm">
              25% of resumes get filtered out of <ATSInfoTooltip plural variant="info" /> due to
              missing keywords.
            </AlertTitle>
          </Alert>
          <VerticalGroup className="gap-4 lg:gap-6">
            {!jobListingId && (
              <>
                <VerticalGroupColumn className="text-[12px]">
                  <VerticalGroupTitle className="text-sm lg:text-base">
                    Choose how you want to optimize your resume
                  </VerticalGroupTitle>
                  <div className="flex flex-col lg:flex-row justify-between gap-3 lg:gap-5">
                    <TailorStyleComponent
                      isSet={!isTailorJobTitle}
                      onClick={() => setIsTailorJobTitle(false)}
                      text="Optimize for Job Application"
                    />
                    <TailorStyleComponent
                      isSet={isTailorJobTitle}
                      onClick={() => setIsTailorJobTitle(true)}
                      text="Optimize for Job Title"
                    />
                  </div>
                </VerticalGroupColumn>
                <div className="border-border-muted border-b"></div>
              </>
            )}
            {showResumeSelection && (
              <VerticalGroupRow>
                <VerticalGroupColumn className="w-1/2">
                  <VerticalGroupTitle>Base Resume</VerticalGroupTitle>
                  <VerticalGroupDescription>
                    Choose which resume to use as the source to optimize your resume.
                  </VerticalGroupDescription>
                </VerticalGroupColumn>
                <VerticalGroupColumn className="w-1/2">
                  <ResumesSelect onValueChange={setSelectedResumeId} value={selectedResumeId} />
                </VerticalGroupColumn>
              </VerticalGroupRow>
            )}
            {!isTailorJobTitle && !jobListingId && (
              <>
                <VerticalGroupColumn>
                  <VerticalGroupTitle className="flex flex-row items-center gap-1">
                    Job Application Source{' '}
                    <Question
                      className="cursor-pointer"
                      onClick={() =>
                        window.open('https://www.usecanyon.com/features/resume-optimizer', '_blank')
                      }
                    />
                  </VerticalGroupTitle>
                  <VerticalGroupDescription className="text-xs lg:text-sm">
                    Choose which job application to optimize your resume to. Applications with job
                    descriptions will be best suited for optimization.
                  </VerticalGroupDescription>
                  <div className="text-muted-foreground mt-3 lg:mt-4 flex flex-row items-center gap-2 text-xs lg:text-sm">
                    <MissingDescriptionDot />
                    <span>– Denotes an application without a job description filled</span>
                  </div>
                  <ApplicationSelect
                    className="mt-2"
                    onValueChange={setSelectedApplicationId}
                    selectItemAppend={(application) =>
                      !application.jobDetails?.length ? (
                        <MissingDescriptionDot />
                      ) : (
                        <DescriptionAvailableDot />
                      )
                    }
                    value={selectedApplicationId}
                  />
                </VerticalGroupColumn>
                {isCreateNewApplication && (
                  <CondensedApplicationForm
                    form={form}
                    onSubmit={handleCreationApplicationSubmit}
                  />
                )}
              </>
            )}
            {isTailorJobTitle && (
              <>
                <VerticalGroupColumn>
                  <VerticalGroupTitle className="flex flex-row items-center gap-1">
                    Job Title{' '}
                    <Question
                      className="cursor-pointer"
                      onClick={() =>
                        window.open('https://www.usecanyon.com/features/resume-optimizer', '_blank')
                      }
                    />
                  </VerticalGroupTitle>
                  <Input
                    className="mt-1"
                    onChange={(e) => setJobTitle(e.target.value)}
                    placeholder="Enter a job title to optimize your resume for"
                    value={jobTitle}
                  />
                </VerticalGroupColumn>
              </>
            )}
          </VerticalGroup>
        </div>
        <DialogFooter className="flex-col-reverse sm:flex-row gap-2 sm:gap-0">
          <DialogClose asChild>
            <Button className="w-full sm:w-auto" size="sm" variant="outline">
              Cancel
            </Button>
          </DialogClose>
          {isCreateNewApplication ? (
            <Button
              className="w-full sm:w-auto"
              disabled={!enableApplicationScreenContinueButton}
              form={CONDENSED_APPLICATION_FORM_ID}
              loading={isLoading}
              size="sm"
              type="submit"
              variant="primary"
            >
              Next
            </Button>
          ) : (
            <Button
              className="w-full sm:w-auto"
              disabled={!enableApplicationScreenContinueButton}
              loading={isLoading}
              onClick={handleInitialScreenSubmit}
              size="sm"
              variant="primary"
            >
              Next
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
      <InnerDialog onOpenChange={setShowKeywordsScreen} open={showKeywordsScreen}>
        <InnerDialogContent className="max-w-[700px] w-[calc(100vw-2rem)] lg:w-full max-h-[85vh] my-4 lg:my-8 flex flex-col overflow-hidden">
          <InnerDialogHeader className="shrink-0">
            <InnerDialogTitle className="text-base lg:text-lg">
              Optimize Resume Keywords
            </InnerDialogTitle>
          </InnerDialogHeader>
          <div
            className="flex-1 min-h-0 overflow-y-auto p-3 lg:p-4"
            onPointerDown={(e) => e.stopPropagation()}
            onPointerMove={(e) => e.stopPropagation()}
            onPointerUp={(e) => e.stopPropagation()}
          >
            <VerticalGroup className="gap-6 lg:gap-8">
              <VerticalGroupColumn>
                <VerticalGroupTitle className="flex flex-row items-center gap-1 text-sm lg:text-base">
                  Job Keywords{' '}
                  <Question
                    className="cursor-pointer"
                    onClick={() =>
                      window.open('https://www.usecanyon.com/features/resume-optimizer', '_blank')
                    }
                  />
                </VerticalGroupTitle>
                <VerticalGroupDescription className="text-xs lg:text-sm">
                  Add any keywords from the job description to your resume (optional).
                </VerticalGroupDescription>
                <InputChips
                  chips={selectedJobKeywords}
                  disableDrag={true}
                  onChange={setSelectedJobKeywords}
                  placeholder="Type a keyword and press enter to add"
                />
                <SuggestedChips
                  chips={suggestedJobKeywords}
                  className={selectedJobKeywords?.length > 0 ? 'mt-2' : ''}
                  emptyMessage={'No keyword suggestions available.'}
                  onSelect={handleSuggestedKeywordAdd}
                  title="Keyword Suggestions"
                />
              </VerticalGroupColumn>
              {missingSkills?.length > 0 && (
                <>
                  <VerticalGroupColumn>
                    <VerticalGroupTitle className="text-sm lg:text-base">
                      Missing Skills
                    </VerticalGroupTitle>
                    <VerticalGroupDescription className="text-xs lg:text-sm">
                      Skills found in the job description that are missing from your resume
                    </VerticalGroupDescription>
                    <InputChips
                      chips={skills}
                      disableDrag={true}
                      onChange={setSkills}
                      placeholder="Type a skill and press enter to add"
                    />
                    <SuggestedChips
                      chips={missingSkills}
                      className={skills?.length > 0 ? 'mt-2' : ''}
                      emptyMessage="No missing skill suggestions remaining."
                      onSelect={handleSuggestedSkillAdd}
                      title="Missing Skill Suggestions"
                    />
                  </VerticalGroupColumn>
                  <VerticalGroupColumn>
                    <VerticalGroupDescription className="text-xs lg:text-sm">
                      <Tooltip>
                        <TooltipTrigger>
                          <div className="flex flex-row items-center gap-1">
                            Automatically choose the best keywords to add to your resume
                            <Question />
                          </div>
                        </TooltipTrigger>
                        <TooltipContent className="text-xs">
                          We'll use the job description to add the best keywords. <br></br>Note:
                          This may add keywords that are not true so please verify it afterwards.
                        </TooltipContent>
                      </Tooltip>
                    </VerticalGroupDescription>
                    <Switch
                      checked={isAutoSelectSkills}
                      onCheckedChange={() => setIsAutoSelectSkills(!isAutoSelectSkills)}
                    />
                  </VerticalGroupColumn>
                </>
              )}
            </VerticalGroup>
          </div>
          <InnerDialogFooter className="shrink-0 flex-col-reverse sm:flex-row gap-2 sm:gap-0">
            <InnerDialogClose asChild>
              <Button className="w-full sm:w-auto" size="sm" variant="outline">
                Back
              </Button>
            </InnerDialogClose>
            <Button
              className="w-full sm:w-auto"
              loading={isLoading}
              onClick={handleOptimizeResumeForJob}
              rightIcon={
                !isPaidPlan && (
                  <span className="rounded-md bg-emerald-500/30 px-1.5 py-0.5 text-xs">
                    {tokens?.optimizeResume}
                  </span>
                )
              }
              size="sm"
              variant="primary"
            >
              Optimize Resume
            </Button>
          </InnerDialogFooter>
        </InnerDialogContent>
      </InnerDialog>
    </Dialog>
  )
}

const MissingDescriptionDot = () => <div className="h-1 w-1 rounded-full bg-yellow-400" />
const DescriptionAvailableDot = () => <div className="h-1 w-1 rounded-full bg-green-400" />
